/**
 * Story E01.12 AC-07 — central logging shim.
 *
 * In **dev** every level lands in the browser console verbatim — easiest
 * during interactive debugging.
 *
 * In **prod** info/warn become no-ops (to keep the user console clean and
 * to avoid leaking implementation detail), while `error` is forwarded to
 * Sentry via `captureException`. Anything we throw at `log.error` should
 * be either an `Error` instance or a string — `Sentry.captureException`
 * accepts either, but Errors carry a stack so prefer those.
 *
 * Why a thin wrapper rather than `console.*` directly:
 *  - Lints can ban `console.*` everywhere except this file.
 *  - The same call site works in dev (console) and prod (Sentry) without
 *    branching at every use.
 *  - `beforeSend` filters in `main.tsx` already drop expected user-flow
 *    errors (auth/forbidden/network), so error-tier calls here stay
 *    focused on truly unexpected failures.
 */
import * as Sentry from '@sentry/react'

const isDev = import.meta.env.DEV

type Loggable = unknown

export const log = {
  info(...args: Loggable[]): void {
    if (isDev) console.info(...args)
  },
  warn(...args: Loggable[]): void {
    if (isDev) console.warn(...args)
  },
  error(err: unknown, ...rest: Loggable[]): void {
    if (isDev) {
      console.error(err, ...rest)
      return
    }
    // Production path: Sentry.captureException is a no-op when Sentry was
    // initialized with `enabled: false` (see main.tsx) so this remains
    // safe even before a DSN is configured (D-05 follow-up).
    Sentry.captureException(err)
  },
}
